import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Facebook, Plug, CheckCircle2, AlertTriangle, Link2Off, ShieldCheck, Building2, ExternalLink, Loader2, Chrome, Bot, Database, Plus, Trash2, Star, RefreshCw, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { SeasonalityConfig, WeatherIntelligenceConfig, MediaPlanEditor } from "@/components/IntelligenceSettings";

const StatusPill = ({ tone, children, ...rest }) => (
  <span {...rest} className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${tone}`}>{children}</span>
);

function MetaConnection() {
  const [status, setStatus] = useState(null);
  const [assets, setAssets] = useState(null);
  const [sel, setSel] = useState({ business_id: "", ad_account_id: "", page_id: "", instagram_user_id: "" });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/meta/status");
      setStatus(data);
      if (data.connected) {
        const a = await api.get("/meta/assets");
        setAssets(a.data.assets || {});
        const s = a.data.selection || {};
        setSel({ business_id: s.business_id || "", ad_account_id: s.ad_account_id || "", page_id: s.page_id || "", instagram_user_id: s.instagram_user_id || "" });
      }
    } catch { setStatus({ configured: false, connected: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = () => { window.location.assign(`${API}/meta/connect`); };

  const saveSelection = async () => {
    if (!sel.business_id || !sel.ad_account_id || !sel.page_id) { toast.error("Choose a Business Portfolio, ad account and Page"); return; }
    setSaving(true);
    try {
      await api.post("/meta/selection", { ...sel, instagram_user_id: sel.instagram_user_id || null });
      toast.success("Meta asset selection saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save selection"); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/meta/disconnect"); toast.success("Meta disconnected"); setAssets(null); load(); }
    catch { toast.error("Could not disconnect"); } finally { setBusy(false); }
  };

  if (!status) return <div className="text-muted-foreground text-sm">Loading Meta connection...</div>;

  const igForPage = (assets?.instagram_accounts || []).filter((i) => !sel.page_id || i.page_id === sel.page_id);

  return (
    <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="meta-connection-card">
      <div className="flex items-center justify-between gap-3 p-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#0866FF]/10 flex items-center justify-center"><Facebook className="w-6 h-6 text-[#0866FF]" /></div>
          <div>
            <div className="font-bold text-navy flex items-center gap-2">Meta (Facebook &amp; Instagram) Ads</div>
            <div className="text-xs text-muted-foreground">Connect your dealership's Meta Business assets to launch and track ads. You keep full ownership.</div>
          </div>
        </div>
        {status.connected
          ? (status.reauth_required
            ? <StatusPill tone="bg-amber-50 text-amber-700 border-amber-200"><AlertTriangle className="w-3.5 h-3.5" /> Reconnect</StatusPill>
            : <StatusPill tone="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="meta-status-connected"><CheckCircle2 className="w-3.5 h-3.5" /> Connected</StatusPill>)
          : <StatusPill tone="bg-slate-100 text-slate-600 border-slate-200" data-testid="meta-status-disconnected"><Plug className="w-3.5 h-3.5" /> Not Connected</StatusPill>}
      </div>

      <div className="p-5 space-y-4">
        {!status.configured && (
          <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3" data-testid="meta-not-configured">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Meta developer app is not configured yet. The Marine Ad AI team needs to add the Meta App credentials before dealerships can connect.</span>
          </div>
        )}

        {!status.connected ? (
          <Button onClick={connect} disabled={!status.configured} className="bg-[#0866FF] hover:bg-[#0757d6] text-white font-bold" data-testid="meta-connect-btn">
            <Facebook className="w-4 h-4 mr-2" /> Connect Meta
          </Button>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Connected by <b className="text-navy">{status.connected_by || "-"}</b></span>
              {status.connected_at && <span>on {new Date(status.connected_at).toLocaleString()}</span>}
              {status.token_expires_at ? <span>Token expires {new Date(status.token_expires_at * 1000).toLocaleDateString()}</span> : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Business Portfolio</Label>
                <Select value={sel.business_id} onValueChange={(v) => setSel((s) => ({ ...s, business_id: v }))}>
                  <SelectTrigger data-testid="meta-select-business"><SelectValue placeholder="Select portfolio" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(assets?.businesses || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Ad Account</Label>
                <Select value={sel.ad_account_id} onValueChange={(v) => setSel((s) => ({ ...s, ad_account_id: v }))}>
                  <SelectTrigger data-testid="meta-select-adaccount"><SelectValue placeholder="Select ad account" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(assets?.ad_accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.currency || ""})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Facebook Page</Label>
                <Select value={sel.page_id} onValueChange={(v) => setSel((s) => ({ ...s, page_id: v, instagram_user_id: "" }))}>
                  <SelectTrigger data-testid="meta-select-page"><SelectValue placeholder="Select Page" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(assets?.pages || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Instagram Account (optional)</Label>
                <Select value={sel.instagram_user_id || "none"} onValueChange={(v) => setSel((s) => ({ ...s, instagram_user_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="meta-select-instagram"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">None</SelectItem>
                    {igForPage.map((i) => <SelectItem key={i.id} value={i.id}>@{i.username}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={saveSelection} disabled={saving} className="bg-navy hover:bg-navy-light text-white font-bold" data-testid="meta-save-selection">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Building2 className="w-4 h-4 mr-1.5" />} Save Selection
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="hover:text-red-600 hover:border-red-300" data-testid="meta-disconnect-btn" disabled={busy}>
                    <Link2Off className="w-4 h-4 mr-1.5" /> Disconnect
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Disconnect Meta?</AlertDialogTitle>
                    <AlertDialogDescription>This removes Marine Ad AI's stored access to your Meta assets and deletes the encrypted token. Your Meta account, Pages and campaigns are unaffected.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="meta-disconnect-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={disconnect} className="bg-red-600 hover:bg-red-700" data-testid="meta-disconnect-confirm">Disconnect</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CHANNEL_ICON = { meta: Facebook, google: Chrome, chatgpt: Bot };
const CHANNEL_BLURB = {
  google: "Publish inventory-driven ads across Google Search, YouTube and Performance Max from the same boat data.",
  chatgpt: "Reach buyers inside ChatGPT with conversational, inventory-driven ads. Integration in progress.",
};

function ComingSoonChannel({ ch }) {
  const Icon = CHANNEL_ICON[ch.id] || Plug;
  return (
    <div className="bg-white rounded-2xl border border-border p-5 flex items-center justify-between gap-3" data-testid={`channel-${ch.id}-card`}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy/5 flex items-center justify-center"><Icon className="w-6 h-6 text-navy" /></div>
        <div>
          <div className="font-bold text-navy">{ch.name}</div>
          <div className="text-xs text-muted-foreground">{CHANNEL_BLURB[ch.id] || "Advertising channel."}</div>
        </div>
      </div>
      <StatusPill tone="bg-amber-50 text-amber-700 border-amber-200" data-testid={`channel-${ch.id}-status`}>Coming Soon</StatusPill>
    </div>
  );
}

function Connections() {
  const [channels, setChannels] = useState(null);
  useEffect(() => { api.get("/channels").then(({ data }) => setChannels(data)).catch(() => setChannels([])); }, []);
  if (!channels) return <div className="text-sm text-muted-foreground">Loading connections...</div>;
  return (
    <div className="space-y-3" data-testid="connections-list">
      {channels.map((ch) => (ch.id === "meta" ? <MetaConnection key="meta" /> : <ComingSoonChannel key={ch.id} ch={ch} />))}
    </div>
  );
}

function InventorySources() {
  const [types, setTypes] = useState([]);
  const [presets, setPresets] = useState([]);
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", connector_type: "", dms_preset: "", config: {} });
  const [dealerId, setDealerId] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/inventory/sources").then(({ data }) => setSources(data)).catch(() => {});
  useEffect(() => {
    api.get("/inventory/connector-types").then(({ data }) => setTypes(data)).catch(() => {});
    api.get("/inventory/dms-presets").then(({ data }) => setPresets(data)).catch(() => {});
    api.get("/dealership").then(({ data }) => setDealerId(data?.id || "")).catch(() => {});
    load();
  }, []);

  const ct = types.find((t) => t.id === form.connector_type);
  const setCfg = (k, v) => setForm((s) => ({ ...s, config: { ...s.config, [k]: v } }));
  const pickPreset = (pid) => {
    const p = presets.find((x) => x.id === pid);
    setForm((s) => ({ ...s, dms_preset: pid, connector_type: p?.connector_type || s.connector_type, config: {} }));
  };

  const save = async () => {
    if (!form.name || !form.connector_type) { toast.error("Name and connector type are required"); return; }
    try {
      await api.post("/inventory/sources", { name: form.name, connector_type: form.connector_type, dms_preset: form.dms_preset || null, config: form.config });
      toast.success("Inventory source added");
      setOpen(false); setForm({ name: "", connector_type: "", dms_preset: "", config: {} }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add source"); }
  };
  const makeSoT = async (id) => { await api.post(`/inventory/sources/${id}/source-of-truth`); toast.success("Source of truth updated"); load(); };
  const del = async (id) => { await api.delete(`/inventory/sources/${id}`); toast.success("Source removed"); load(); };
  const runSync = async (id, test) => {
    setBusy(id + (test ? ":test" : ":sync"));
    try {
      const { data } = await api.post(`/inventory/sources/${id}/sync${test ? "?test=true" : ""}`);
      if (test) toast.success(`Connection OK: ${data.records_fetched} record(s) found`);
      else toast.success(`Synced: ${data.created} added, ${data.updated} updated (${data.records_fetched} fetched)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setBusy(""); }
  };
  const copyDealerId = () => { navigator.clipboard?.writeText(dealerId); toast.success("RecJet Dealer ID copied"); };
  const toggleAuto = async (id, enabled) => {
    try { await api.post(`/inventory/sources/${id}/auto-sync`, { enabled }); toast.success(enabled ? "Hourly auto-sync on" : "Auto-sync off"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update auto-sync"); }
  };

  return (
    <div className="space-y-3" data-testid="inventory-sources">
      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-navy/5 flex items-center justify-center"><Database className="w-6 h-6 text-navy" /></div>
            <div>
              <div className="font-bold text-navy">Inventory Sources</div>
              <div className="text-xs text-muted-foreground">Connect any DMS or feed. Marine Ad AI normalizes every source into one inventory model.</div>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="font-semibold" data-testid="add-inventory-source"><Plus className="w-4 h-4 mr-1.5" /> Add Source</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle>Add Inventory Source</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                <div className="space-y-1.5">
                  <Label className="text-xs">Source Name</Label>
                  <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Main Store Winboats Feed" data-testid="src-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">DMS Preset (optional)</Label>
                  <Select value={form.dms_preset} onValueChange={pickPreset}>
                    <SelectTrigger data-testid="src-preset"><SelectValue placeholder="Choose a DMS or leave blank" /></SelectTrigger>
                    <SelectContent className="bg-white">{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.status === "first_planned" ? " (coming soon)" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Connector Type</Label>
                  <Select value={form.connector_type} onValueChange={(v) => setForm((s) => ({ ...s, connector_type: v, config: {} }))}>
                    <SelectTrigger data-testid="src-connector-type"><SelectValue placeholder="Select connector type" /></SelectTrigger>
                    <SelectContent className="bg-white">{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {(ct?.fields || []).map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-xs">{f.label}{f.secret ? " (stored securely)" : ""}</Label>
                    {f.type === "select" ? (
                      <Select value={form.config[f.key] || ""} onValueChange={(v) => setCfg(f.key, v)}>
                        <SelectTrigger data-testid={`src-field-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-white">{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={f.secret ? "password" : (f.type === "number" ? "number" : "text")}
                        value={form.config[f.key] || ""} onChange={(e) => setCfg(f.key, e.target.value)} data-testid={`src-field-${f.key}`} />
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={save} className="bg-navy hover:bg-navy-light text-white font-bold" data-testid="src-save">Save Source</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {dealerId && (
          <div className="flex items-center justify-between gap-3 px-5 py-3 bg-navy/5 border-b border-border" data-testid="recjet-dealer-id-row">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-navy">RecJet Dealer ID</div>
              <div className="text-xs text-muted-foreground truncate">Paste this into the source website so its data is linked to this dealership.</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <code className="text-xs font-mono bg-white border border-border rounded px-2 py-1" data-testid="recjet-dealer-id-value">{dealerId}</code>
              <Button variant="ghost" size="sm" onClick={copyDealerId} data-testid="copy-dealer-id"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        <div className="divide-y divide-border" data-testid="inventory-sources-list">
          {sources.length === 0 ? (
            <div className="p-5 text-sm text-muted-foreground">No inventory sources yet. Your boats are managed manually until you connect a DMS or feed.</div>
          ) : sources.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 p-4" data-testid={`inventory-source-${s.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-navy flex items-center gap-2 truncate">
                  {s.name}
                  {s.is_source_of_truth && <StatusPill tone="bg-emerald-50 text-emerald-700 border-emerald-200"><Star className="w-3 h-3" /> Source of Truth</StatusPill>}
                </div>
                <div className="text-xs text-muted-foreground">{(types.find((t) => t.id === s.connector_type) || {}).name || s.connector_type} · {s.sync_status}{s.last_synced_at ? ` · last sync ${new Date(s.last_synced_at).toLocaleString()}` : ""}</div>
                {s.sync_error && <div className="text-xs text-red-600 mt-0.5" data-testid={`src-error-${s.id}`}>{s.sync_error}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {s.connector_type === "rest_api" && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => runSync(s.id, true)} disabled={!!busy} data-testid={`src-test-${s.id}`}>
                      {busy === s.id + ":test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                      <span className="ml-1.5 hidden sm:inline">Test</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => runSync(s.id, false)} disabled={!!busy} data-testid={`src-sync-${s.id}`}>
                      {busy === s.id + ":sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      <span className="ml-1.5 hidden sm:inline">Sync</span>
                    </Button>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" title="Automatically pull this feed every hour" data-testid={`src-autosync-label-${s.id}`}>
                      <Switch checked={s.auto_sync !== false} onCheckedChange={(v) => toggleAuto(s.id, v)} data-testid={`src-autosync-${s.id}`} />
                      <span className="hidden md:inline">Hourly</span>
                    </label>
                  </>
                )}
                {!s.is_source_of_truth && <Button variant="ghost" size="sm" onClick={() => makeSoT(s.id)} data-testid={`src-sot-${s.id}`}><Star className="w-4 h-4" /></Button>}
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(s.id)} data-testid={`src-del-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const m = params.get("meta");
    if (m === "connected") toast.success("Meta connected successfully");
    else if (m === "error") toast.error(`Meta connection failed${params.get("reason") ? `: ${params.get("reason")}` : ""}`);
    if (m) { params.delete("meta"); params.delete("reason"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6 fade-up" data-testid="settings-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-navy">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage integrations and your dealership profile.</p>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Dealership</h2>
        <div className="bg-white rounded-2xl border border-border p-5 flex items-center justify-between gap-3">
          <div>
            <div className="font-bold text-navy">Dealership Profile &amp; Branding</div>
            <div className="text-xs text-muted-foreground">Logo, contact details, brands carried, and marketing preferences.</div>
          </div>
          <Button variant="outline" onClick={() => navigate("/profile")} data-testid="settings-open-profile">
            Open <ExternalLink className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Connections</h2>
        <Connections />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Inventory</h2>
        <InventorySources />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">RecJet Mind Dealer Profile</h2>
        <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-4" data-testid="dealer-profile-link-card">
          <div>
            <div className="font-semibold text-navy">Dealer Intelligence Profile</div>
            <p className="text-sm text-muted-foreground mt-0.5">Market, inventory priorities, floorplan defaults, buyer profile and marketing preferences that RecJet Mind uses. Optional and editable anytime.</p>
          </div>
          <Button asChild variant="outline" className="shrink-0" data-testid="open-dealer-profile">
            <a href="/dealer-profile">Open profile</a>
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">RecJet Mind Intelligence</h2>
        <SeasonalityConfig />
        <MediaPlanEditor />
        <WeatherIntelligenceConfig />
      </section>
    </div>
  );
}
